'use client'

import React, { useEffect, useRef, useState } from 'react'
import { getClassNames } from '@helpers/productWidgetHelper'
import Image from 'next/image'
import Link from 'next/link'
import { CustomerStoriesProps } from '@interfaces/brandHub'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { EVENTS } from '@configs/events.config'
import { TENANT_TYPE } from '@components/Ackodrive/common/constants'
import { useAnimateOnVisible } from '@hooks/useAnimateOnVisible'
import {
  blackQuote,
  borderedArrowUrl,
  CAROUSEL_INTERVAL,
  CustomerStoriesHeadingMock,
  hoverArrowUrl,
} from '@components/BrandHub/BrandHubMock'
import styles from './CustomerStoriesCarousel.module.scss'

const CustomerStories = ({ url, inputStories }: CustomerStoriesProps) => {
  const moreThanOneStory = inputStories.length > 1
  const stories = moreThanOneStory ? [...inputStories, inputStories[0]] : inputStories
  const length = inputStories.length

  const [currentInd, setCurrentInd] = useState(0)
  const [transition, setTransition] = useState(true)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const touchStartXRef = useRef<number | null>(null)
  const isSwipingRef = useRef<Boolean>(false)

  const { ref, animationClassName } = useAnimateOnVisible<HTMLDivElement>()

  const changeStory = (newInd: number) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setCurrentInd(newInd % stories.length)
    timeoutRef.current = setTimeout(() => changeStory(newInd + 1), CAROUSEL_INTERVAL)
  }

  useEffect(() => {
    if (moreThanOneStory) timeoutRef.current = setTimeout(() => changeStory(currentInd + 1), CAROUSEL_INTERVAL)
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  const handleSwipeStart = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartXRef.current = e.touches[0].clientX
    isSwipingRef.current = true
  }

  const handleSwipeEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isSwipingRef.current || touchStartXRef.current === null) return
    const deltaX = e.changedTouches[0].clientX - touchStartXRef.current

    if (Math.abs(deltaX) > 50) {
      if (deltaX < 0) {
        // Swipe left
        changeStory(currentInd + 1)
      } else {
        // Swipe right
        const newInd = currentInd - 1 < 0 ? length - 1 : currentInd - 1
        changeStory(newInd)
      }
    }

    touchStartXRef.current = null
    isSwipingRef.current = false
  }

  useEffect(() => {
    if (!transition) {
      requestAnimationFrame(() => {
        setTransition(true)
      })
    }
  }, [transition])

  const handleClick = async (url: string) => {
    await trackEvent(EVENTS[TENANT_TYPE.BRANDHUB].engage.eventname, {
      ...EVENTS[TENANT_TYPE.BRANDHUB].engage.eventProps,
      journey: 'customer_stories',
      redirection_url: url,
    })
  }

  return (
    <div
      ref={ref}
      className={`${styles.storiesContainer} ${animationClassName}`}
      style={{
        ['--cur-i' as any]: currentInd,
      }}>
      <div className={styles.customerHeadingContainer}>
        <p className={styles.customerHeading}>{CustomerStoriesHeadingMock.title}</p>
        <p className={styles.customerSubHeading}>{CustomerStoriesHeadingMock.description}</p>
      </div>
      <div className={styles.carouselContainer}>
        <div
          className={styles.carousel}
          onTouchStart={handleSwipeStart}
          onTouchEnd={handleSwipeEnd}
          onTransitionEnd={() => {
            if (currentInd >= length) {
              setTransition(false)
              changeStory(0)
            }
          }}
          style={{
            transition: transition ? 'transform 500ms ease-out' : '',
          }}>
          {stories.map((story, i) => (
            <div key={i} className={styles.carouselItem}>
              <div className={styles.customerStory}>
                <Image className={styles.customerProfileImg} src={blackQuote} alt="Quote" height={22} width={28} />
                <p>{story.story}</p>
              </div>
              <div className={styles.customerProfile}>
                {story.customerProfile?.data ? (
                  <Image
                    src={story.customerProfile.data?.attributes?.url}
                    alt="Customer Profile"
                    height={56}
                    width={56}
                  />
                ) : (
                  <p className={styles.customerProfileImg}> {story.customerName.charAt(0).toUpperCase()}</p>
                )}
                <div className={styles.customerDetail}>
                  <p className={styles.customerName}>{story.customerName}</p>
                  <p className={styles.customerTitle}>{story.customerTitle}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className={styles.customerBottom}>
        <Link
          href={url}
          target="_blank"
          className={styles.arrow}
          onClick={() => {
            void handleClick(url)
          }}>
          <Image src={borderedArrowUrl} alt="right arrow" height={48} width={48} />
          <Image src={hoverArrowUrl} alt="right arrow" height={48} width={48} />
        </Link>

        {moreThanOneStory && (
          <div className={styles.dotContainer}>
            {Array(length)
              .fill(0)
              .map((_, i) => (
                <div
                  key={i}
                  className={getClassNames([styles.dot], {
                    [styles.activeDot]: i === currentInd || (i === 0 && length === currentInd),
                  })}
                  onClick={() => changeStory(i)}
                />
              ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default CustomerStories
